import {createStyles} from '@mantine/core';
import React from 'react';

import type {SidepanelChat} from '../ChatRouter';
import ChatSidebarNav from './ChatSidebarNav';
import GroupedChatListing from './GroupedChatListing';
import RedesignSidebarToolbar from './RedesignSidebarToolbar';

/**
 * Fixed-width inner content. The parent drawer animates its own width (and clips via overflow:hidden)
 * to reveal/hide the sidebar, so this content keeps a constant width and is clipped rather than
 * reflowing during the animation.
 */
const SIDEBAR_CONTENT_WIDTH = 175;

const useStyles = createStyles(() => ({
  root: {
    width: SIDEBAR_CONTENT_WIDTH,
    flex: 'none',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  divider: {
    height: 1,
    flex: 'none',
    backgroundColor: '#D6DEFF',
    // Negative horizontal margins span the drawer's padding so the rule reaches both edges.
    margin: '14px -14px 22px',
  },
  listing: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export interface RedesignChatSidebarProps {
  selectedChatSession: SidepanelChat | null;
  onSelectChatSession: (chat: SidepanelChat) => void;
  /** Start a new chat (mirrors the header "New chat" action). */
  onNewChat: () => void;
  refetchRef: React.MutableRefObject<() => void>;
}

/**
 * Contents of the redesigned chat sidebar (LLM_ENABLE_CHAT_REDESIGN): a top toolbar (search +
 * settings), the primary navigation list, then the grouped (Starred / Recent) chat history.
 */
export default function RedesignChatSidebar({
  selectedChatSession,
  onSelectChatSession,
  onNewChat,
  refetchRef,
}: RedesignChatSidebarProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.root}>
      <RedesignSidebarToolbar />
      <div className={classes.divider} />
      <ChatSidebarNav onNewChat={onNewChat} />
      <div className={classes.listing}>
        <GroupedChatListing
          selectedChatSession={selectedChatSession}
          onSelectChatSession={onSelectChatSession}
          refetchRef={refetchRef}
        />
      </div>
    </div>
  );
}
